// Shared recipe for the row's three action buttons — a plain style object
// (not a registered component), spread into each declaration below. Same
// convention as `_chipStyle` in components/ReminderComposer.js. All three
// inherit IconButton; `icon:` overrides SquareButton's default ('smile' —
// the stray smiley that shipped next to the trash glyph, 2026-08-01 per
// Nika "fix delete icon"; never add a second Icon child on IconButton).
//
// Visible at full contrast, with the shared control-size tap target. Hover
// changes the surface without fading the action's icon.
const _actionBtnStyle = {
  extends: 'IconButton',
  flexShrink: '0',
  color: 'inactive',
  width: 'controlHCompact',
  height: 'controlHCompact',
  minWidth: 'controlHCompact',
  minHeight: 'controlHCompact',
  padding: '0',
  boxSizing: 'border-box',
  ':hover': { background: 'hover' },
  '@coarse': { width: 'controlH', height: 'controlH', minWidth: 'controlH', minHeight: 'controlH' }
}

// One row inside a Reminders group (pages/reminders.js). Mounted via
// `childExtends: 'ReminderRow'` + `childrenAs: 'state'` over a dynamically
// generated list — per the v3.14 list-row quirk, the ROW ITSELF carries no
// onClick (that can fail to bind in prod for childExtends+reactive-children
// rows). Interaction lives on its two STATIC named children instead
// (CompletionButton, DeleteBtn) — those bind reliably because they're declared once
// here, not per-item generated.
export const ReminderRow = {
  flow: 'x',
  align: 'center',
  gap: 'Z1',
  padding: 'Z A',
  round: 'radiusCard',
  ':hover': { background: 'hover' },
  onRender: (el) => el.call('armLongPress'),
  onContextmenu: (event, el, s) =>
    el.call('openAppContextMenu', event, { kind: 'reminder', id: s.id }),

  // A dedicated button key avoids auto-extending Checkbox, whose hidden
  // Input would otherwise create a second, unnamed keyboard target here.
  CompletionButton: {
    extends: 'IconButton',
    icon: 'checkmark',
    type: 'button',
    flexShrink: '0',
    width: 'controlHCompact',
    height: 'controlHCompact',
    minWidth: 'controlHCompact',
    minHeight: 'controlHCompact',
    padding: '0',
    boxSizing: 'border-box',
    '@coarse': { width: 'controlH', height: 'controlH', minWidth: 'controlH' },
    round: 'radiusPill',
    borderWidth: 'hairline2',
    borderStyle: 'dashed',
    borderColor: (el, s) => (s.done ? 'transparent' : 'inactive'),
    cursor: 'pointer',
    // The theme supplies BOTH ink and fill. A flat transparent background
    // overrode the completed fill, making its reversed checkmark invisible.
    theme: (el, s) => (s.done ? 'primary' : 'tertiary'),
    'aria-label': (el) => el.call('polyglot', 'workspace.reminders.toggleAria2'),
    ariaPressed: (el, s) => (s.done ? 'true' : 'false'),
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('toggleReminder', s.id)
    },
    Icon: {
      width: 'iconXs',
      height: 'iconXs',
      flexShrink: '0',
      color: 'currentColor',
      pointerEvents: 'none',
      opacity: (el, s) => (s.done ? '1' : '0')
    }
  },

  Title: {
    tag: 'span',
    flex: '1',
    minWidth: '0',
    fontSize: 'A',
    lineHeight: '1.4',
    color: (el, s) => (s.done ? 'caption' : 'title'),
    textDecoration: (el, s) => (s.done ? 'line-through' : 'none'),
    // Primary text wraps — never cut behind an ellipsis (the overflow law).
    overflowWrap: 'anywhere',
    text: (el, s) => s.title || el.call('polyglot', 'workspace.reminders.untitled')
  },

  DueChip: {
    hide: (el, s) => s.done || !el.call('reminderDueLabel', s.dueAt),
    tag: 'span',
    flexShrink: '0',
    fontSize: 'Y2',
    fontWeight: '700',
    lineHeight: '1',
    padding: 'V Y',
    round: 'radiusPill',
    whiteSpace: 'nowrap',
    color: (el, s) => (el.call('reminderIsOverdue', s.dueAt, s.done) ? 'red' : 'caption'),
    background: (el, s) => (el.call('reminderIsOverdue', s.dueAt, s.done) ? 'red.15' : 'overlay'),
    text: (el, s) => el.call('reminderDueLabel', s.dueAt) || ''
  },

  // Shared-visibility marker + owner attribution for teammates' shared rows.
  SharedChip: {
    hide: (el, s) => s.visibility !== 'shared',
    flow: 'x',
    align: 'center',
    gap: 'W',
    flexShrink: '0',
    fontSize: 'Y2',
    color: 'caption',
    title: (el) => el.call('polyglot', 'workspace.reminders.sharedChip'),
    GlobeIcon: { extends: 'Icon', name: 'globeOutline', boxSize: 'iconXs' },
    Owner: {
      tag: 'span',
      hide: (el, s) => el.call('reminderIsMine', s.root, s),
      text: (el, s) => {
        const p = (s.root?.people || []).find(
          (x) => (x.email || '').toLowerCase() === (s.ownerEmail || '').toLowerCase()
        )
        return (p && (p.name || '').split(/\s+/)[0]) || (s.ownerEmail || '').split('@')[0]
      }
    }
  },

  // Action cluster — see `_actionBtnStyle` above for the shared recipe.
  // Convert MOVES the item (a ticket is made, the reminder is deleted): owner-
  // only like the delete, and it asks first — the same ask as the ⋯ menu's
  // (functions/reminders.js confirmConvertReminderToTask).
  ConvertTaskBtn: {
    ..._actionBtnStyle,
    icon: 'checkOval',
    title: (el) => el.call('polyglot', 'workspace.reminders.convertToTask'),
    'aria-label': (el) => el.call('polyglot', 'workspace.reminders.convertToTask'),
    hide: (el, s) => !el.call('reminderIsMine', s.root, s),
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('confirmConvertReminderToTask', s.id)
    }
  },

  ConvertEventBtn: {
    ..._actionBtnStyle,
    icon: 'dateOutline',
    title: (el) => el.call('polyglot', 'workspace.reminders.convertToEvent'),
    'aria-label': (el) => el.call('polyglot', 'workspace.reminders.convertToEvent'),
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('convertReminderToEvent', s.id)
    }
  },

  DeleteBtn: {
    ..._actionBtnStyle,
    icon: 'trash',
    // Destructive action reads red on hover instead of the neutral tint.
    ':hover': { background: 'red.1', color: 'red' },
    title: (el) => el.call('polyglot', 'workspace.reminders.delete'),
    'aria-label': (el) => el.call('polyglot', 'workspace.reminders.delete'),
    // Owner-only — teammates can complete a shared reminder but not delete it.
    hide: (el, s) => !el.call('reminderIsMine', s.root, s),
    // The house confirm first — the same one the ⋯ menu's Delete opens
    // (functions/reminders.js confirmDeleteReminder).
    onClick: (e, el, s) => {
      e.stopPropagation()
      el.call('confirmDeleteReminder', s.id)
    }
  },

  More: {
    extends: 'AppContextMenuButton',
    'aria-label': (el) => el.call('polyglot', 'workspace.reminders.moreAria'),
    onClick: (event, el, s) =>
      el.call('openAppContextMenu', event, { kind: 'reminder', id: s.id }, { anchor: true })
  }
}

// One group section inside the /reminders body (Overdue / Today / Tomorrow /
// Upcoming / No date / Completed) — mounted via `childExtends:
// 'ReminderGroupSection'` + `childrenAs: 'state'` over `reminderGroups(root)`
// (functions/reminders.js). State shape: { key, label, tone?, muted?, items }.
// Nested factories read `s.<field>` directly — state cascades through plain
// (non childrenAs) nested children, same as PeopleGroupBlock.PgHead/PgGrid
// (components/PersonCard.js), the closest precedent for this exact shape.
export const ReminderGroupSection = {
  flow: 'y',
  gap: 'Z',

  GroupHead: {
    flow: 'x',
    align: 'baseline',
    // `V` collided the count with the label ("NO DATE1") — the label's own
    // 0.08em letter-spacing already eats most of a V gap.
    gap: 'X',

    Label: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '700',
      color: (el, s) => (s.tone === 'error' ? 'red' : 'caption'),
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    Count: {
      tag: 'span',
      fontSize: 'Y2',
      color: 'caption',
      text: (el, s) => String((s.items || []).length)
    }
  },

  Rows: {
    flow: 'y',
    gap: 'V',
    childExtends: 'ReminderRow',
    childrenAs: 'state',
    children: (el, s) => s.items || []
  }
}
